import React, { useEffect, useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useNotify } from "react-admin";
import { organizationsApi, userApi } from "../../providers/env";
import { OrganizationCard } from "../../components/OrganizationCard";
import { CreateOrganizationModal } from "../../components/CreateOrganizationModal";
import {OrganizationStats} from "./types";


interface OrganizationsTabProps {
  onOrgUpdated?: () => void;
}

export const OrganizationsTab: React.FC<OrganizationsTabProps> = ({ onOrgUpdated }) => {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [organizationStats, setOrganizationStats] = useState<
    OrganizationStats[]
  >([]);
  const [openCreate, setOpenCreate] = useState(false);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await organizationsApi.getOrganizations();
      const orgs = response.data || [];

      // Fetch all users once (more efficient than per-org calls)
      let allUsers: any[] = [];
      try {
        const usersResponse = await userApi.getUsers({
          skip: 0,
          limit: 10000, // Get all users
        });
        allUsers = usersResponse.data || [];
      } catch (error) {
        console.error("Error fetching users:", error);
      }

      // Calculate stats for each organization
      const stats = orgs.map((org) => {
        const orgUsers = allUsers.filter(
          (u: any) => u.organization_id === org.id
        );

        // Count sub admins (check role name case-insensitively)
        const subAdminCount = orgUsers.filter((u: any) => {
          const roleName = (u.role?.name || "").toLowerCase();
          return roleName.includes("sub") || 
                 roleName.includes("org admin") || 
                 roleName.includes("admin");
        }).length;

        // Count regular users (those not admins)
        const userCount = orgUsers.filter((u: any) => {
          const roleName = (u.role?.name || "").toLowerCase();
          // User is a regular user if no role or role doesn't include admin
          return !roleName.includes("admin") && !roleName.includes("sub");
        }).length;

        return {
          organization: org,
          sub_admin_count: subAdminCount,
          user_count: userCount,
          total_agents: 0,
        };
      });

      setOrganizationStats(stats);
    } catch (error: any) {
      notify("Error loading organizations", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography>Loading organizations...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {organizationStats.length} total organizations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreate(true)}
        >
          Create Organization
        </Button>
      </Box>

      <Grid container spacing={3}>
        {organizationStats.map((stats) => (
          <Grid item xs={12} sm={6} md={4} key={stats.organization.id}>
            <OrganizationCard stats={stats} onUpdated={() => { fetchOrganizations(); onOrgUpdated?.(); }} />
          </Grid>
        ))}
      </Grid>

      <CreateOrganizationModal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onSuccess={() => { fetchOrganizations(); onOrgUpdated?.(); }}
      />
    </Box>
  );
};